<script>
    // Layout
    import Authenticated from "@/Layouts/Authenticated";

    // Packages
    import {Head} from "@inertiajs/vue3";
    import {useForm, usePage} from '@inertiajs/vue3';

    // Components
    import Button from "@/Components/Button";
    import Pagination from "@/Components/Pagination";
    import SettingsTabs from "@/Components/SettingsTabs";
    import SearchFilter from "@/Components/SearchFilter";
    import SlideOutErrors from "@/Components/SlideOutErrors";
    import CreateCountry from "@/Components/SlideOuts/CreateCountry";
    import CreateRegions from "@/Components/SlideOuts/CreateRegions";
    import CreateVATRates from "@/Components/SlideOuts/CreateVATRates";
    import CreateTradingDetail from "@/Components/SlideOuts/CreateTradingDetail";
    import CreateTradingBankDetails from "@/Components/SlideOuts/CreateTradingBankDetails.vue";

    // Icons
    import {PlusSmIcon as PlusSmIconSolid} from '@heroicons/vue/solid';
    import {PencilAltIcon, CheckCircleIcon} from "@heroicons/vue/outline";

    export default {
        layout: Authenticated,
        components: {
            Head,
            Button,
            Pagination,
            SettingsTabs,
            SearchFilter,
            PencilAltIcon,
            CreateRegions,
            CreateCountry,
            CreateVATRates,
            SlideOutErrors,
            PlusSmIconSolid,
            CheckCircleIcon,
            CreateTradingDetail,
            CreateTradingBankDetails
        },
        props: [
            'trading_details',
            'currencies',
            'bank_details'
        ],
        data() {
            return {
                form: {
                    search: null
                }
            }
        },
        methods: {
            openTradingDetail(trading_detail) {
                this.$refs.createTradingDetailsSlideout.updating = trading_detail !== null;
                this.$refs.createTradingDetailsSlideout.trading_details = this.trading_details;
                this.$refs.createTradingDetailsSlideout.form.id = trading_detail !== null ? trading_detail.id : null;
                this.$refs.createTradingDetailsSlideout.form.trading_name = trading_detail !== null ? trading_detail.trading_name : null;
                this.$refs.createTradingDetailsSlideout.form.display_name = trading_detail !== null ? trading_detail.display_name : null;
                this.$refs.createTradingDetailsSlideout.form.company_registration = trading_detail !== null ? trading_detail.company_registration : null;
                this.$refs.createTradingDetailsSlideout.form.company_vat_number = trading_detail !== null ? trading_detail.company_vat_number : null;
                this.$refs.createTradingDetailsSlideout.form.unit4_element2 = trading_detail !== null ? trading_detail.unit4_element2 : null;
                this.$refs.createTradingDetailsSlideout.form.unit4_element3 = trading_detail !== null ? trading_detail.unit4_element3 : null;
                this.$refs.createTradingDetailsSlideout.form.stand_alone_entity = trading_detail !== null ? trading_detail.stand_alone_entity : null;
                this.$refs.createTradingDetailsSlideout.form.related_trading_entity_id = trading_detail !== null ? trading_detail.related_trading_entity_id : null;
                this.$refs.createTradingDetailsSlideout.form.revenue_invoice_email = trading_detail !== null ? trading_detail.revenue_invoice_email : null;
                this.$refs.createTradingDetailsSlideout.form.supplier_invoice_inbox = trading_detail !== null ? trading_detail.supplier_invoice_inbox : null;
                this.$refs.createTradingDetailsSlideout.form.unit4_company_id = trading_detail !== null ? trading_detail.unit4_company_id : null;
                this.$refs.createTradingDetailsSlideout.form.currency_id = trading_detail !== null ? trading_detail.currency_id : null;
                this.$refs.createTradingDetailsSlideout.currencies = this.currencies;
                this.$refs.createTradingDetailsSlideout.form.multiple_tax_rates = trading_detail !== null ? trading_detail.multiple_tax_rates : null;
                this.$refs.createTradingDetailsSlideout.form.tax_wording = trading_detail !== null ? trading_detail.tax_wording : null;
                this.$refs.createTradingDetailsSlideout.open();
            },
            deleteTradingDetail(id) {
                this.$swal.fire({
                    title: 'Archive Trading Detail',
                    html: "You are archiving a trading detail. <br/>Are you sure you'd like to do this?",
                    showCloseButton: true,
                    showCancelButton: true,
                    confirmButtonText: 'Yes',
                    cancelButtonText: 'No'
                }).then((result) => {
                    if (result.isConfirmed) {
                        // Delete the trading detail
                        this.$inertia.delete(route('admin.settings.trading-details.trading-detail.delete', id), {
                            preserveScroll: true
                        });
                    }
                });
            },
            editTradingBankDetails(trading_details) {
                this.$refs.createTradingBankDetailsSlideOut.trading_details = trading_details;
                this.$refs.createTradingBankDetailsSlideOut.currencies = this.currencies;
                this.$refs.createTradingBankDetailsSlideOut.bank_details = this.bank_details;
                this.$refs.createTradingBankDetailsSlideOut.form.id = trading_details.id;
                this.$refs.createTradingBankDetailsSlideOut.open();
            },
        },
        setup(prop, context) {
            const form = useForm({
                trading_name: "",
                display_name: "",
                company_registration: "",
                company_vat_number: ""
            })

            function submit() {
                this.form.post(route('admin.settings.trading-details.create'), {
                    onSuccess: page => {
                        this.show = false;
                        this.showErrors = false;
                        this.form.reset();
                    },
                    onError: errors => {
                        this.showErrors = true;
                    }
                });
            }

            return {
                form,
                submit
            }
        }
    }
</script>

<template>
    <Head title="Trading Details"/>
    <div class="w-full flex justify-between items-center">
        <h1 class="mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">Trading Details</h1>
        <Button v-on:click="openTradingDetail(null)" 
            class="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 rounded-md text-white bg-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amaze-coral">
            <svg class="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            Add Trading Details
        </Button>
    </div>
    <SettingsTabs/>
    <div class="bg-white py-6 px-6 sm:p-6 shadow">
        <div class="flex flex-col mt-3 space-y-3">
            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                                <tr>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Trading Name
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Display Name
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Company Registration
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Company VAT Number
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-centre text-xs text-gray-500 uppercase tracking-wider">
                                        Bank Accounts
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-centre text-xs text-gray-500 uppercase tracking-wider">
                                        Stand Alone
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="Object.values(trading_details).length > 0" v-for="(trading_detail, index) in trading_details" :key="index" class="hover:bg-gray-50 cursor-pointer">
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900" @click="openTradingDetail(trading_detail)">
                                        {{ trading_detail.trading_name }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900" @click="openTradingDetail(trading_detail)">
                                        {{ trading_detail.display_name }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900" @click="openTradingDetail(trading_detail)">
                                        {{ trading_detail.company_registration }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900" @click="openTradingDetail(trading_detail)">
                                        {{ trading_detail.company_vat_number }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-center">
                                        <div v-if="trading_detail.stand_alone_entity" class="w-full flex justify-center items-center gap-3" @click="editTradingBankDetails(trading_detail)">
                                            <span class="underline text-pitch-purple">
                                                {{ Object.keys(trading_detail.bank_accounts).length }}
                                            </span>
                                            <button @click="editTradingBankDetails(trading_detail)" type="button"
                                                class="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-pitch-purple hover:bg-dark-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                                <PlusSmIconSolid class="h-4 w-4 text-white" aria-hidden="true"/>
                                            </button>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900" @click="openTradingDetail(trading_detail)">
                                        <div class="w-full flex justify-center items-center gap-3">
                                            <CheckCircleIcon v-if="trading_detail.stand_alone_entity" class="h-5 w-5 text-green-400" aria-hidden="true" />
                                        </div>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900" @click="openTradingDetail(trading_detail)">
                                        <PencilAltIcon class="w-5 h-5 text-light-pitch-purple"></PencilAltIcon>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <span class="text-red-600 hover:text-red-900 float-right"
                                            @click="deleteTradingDetail(trading_detail)">
                                            <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none"
                                                viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                                            </svg>
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <CreateTradingDetail ref="createTradingDetailsSlideout"/>
    <CreateTradingBankDetails ref="createTradingBankDetailsSlideOut"/>
</template>